import {useEffect, useMemo, useState, useRef} from "react";

const MAX_ENTRIES = 60;

const formatArg = arg => {
  if (typeof arg === "string") return arg;

  if (arg instanceof Error) {
    return `${arg.name}: ${arg.message}`;
  }

  try {
    return JSON.stringify(arg, null, 2);
  } catch (error) {
    return String(arg);
  }
};

function DebugConsole() {
  const [isOpen, setIsOpen] = useState(false);
  const [entries, setEntries] = useState([]);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    if (process.env.NODE_ENV === "production") return undefined;

    const pushEntry = (level, args) => {
      const message = args.map(formatArg).join(" ");

      // Defer state update to avoid "setState in render" warning
      setTimeout(() => {
        if (!isMounted.current) return;
        setEntries(prev => {
          const next = prev.concat({
            id: `${Date.now()}-${Math.random()}`,
            level,
            time: new Date().toLocaleTimeString(),
            message,
          });

          return next.slice(-MAX_ENTRIES);
        });
      }, 0);
    };

    const originalConsole = {
      log: console.log,
      info: console.info,
      warn: console.warn,
      error: console.error,
    };

    console.log = (...args) => {
      pushEntry("log", args);
      originalConsole.log(...args);
    };

    console.info = (...args) => {
      pushEntry("info", args);
      originalConsole.info(...args);
    };

    console.warn = (...args) => {
      pushEntry("warn", args);
      originalConsole.warn(...args);
    };

    console.error = (...args) => {
      pushEntry("error", args);
      originalConsole.error(...args);
    };

    const handleError = event => pushEntry("error", [event.message]);
    const handleRejection = event =>
      pushEntry("error", [event.reason || "Unhandled promise rejection"]);

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      isMounted.current = false;
      console.log = originalConsole.log;
      console.info = originalConsole.info;
      console.warn = originalConsole.warn;
      console.error = originalConsole.error;
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  const visibleEntries = useMemo(() => entries.slice().reverse(), [entries]);

  if (process.env.NODE_ENV === "production") return null;

  return (
    <div style={{position: "fixed", right: 12, bottom: 12, zIndex: 99999}}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          padding: "8px 12px",
          borderRadius: 8,
          border: "1px solid #444",
          background: "#111",
          color: "#fff",
          cursor: "pointer",
          marginBottom: 8,
        }}>
        {isOpen ? "Fechar logs" : `Logs (${entries.length})`}
      </button>

      {isOpen && (
        <div
          style={{
            width: 460,
            maxWidth: "calc(100vw - 24px)",
            height: 320,
            overflow: "auto",
            background: "rgba(0, 0, 0, 0.92)",
            color: "#fff",
            border: "1px solid #333",
            borderRadius: 10,
            padding: 12,
            fontSize: 12,
            lineHeight: 1.4,
            whiteSpace: "pre-wrap",
          }}>
          {visibleEntries.length === 0 ? (
            <p style={{margin: 0}}>Sem logs capturados.</p>
          ) : (
            visibleEntries.map(entry => (
              <div
                key={entry.id}
                style={{
                  marginBottom: 10,
                  paddingBottom: 10,
                  borderBottom: "1px solid #222",
                }}>
                <strong
                  style={{
                    color:
                      entry.level === "error"
                        ? "#ff7b7b"
                        : entry.level === "warn"
                        ? "#ffd166"
                        : "#7bdff2",
                  }}>
                  [{entry.time}] {entry.level.toUpperCase()}
                </strong>
                <div>{entry.message}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default DebugConsole;

